//@ts-strict-ignore
import React from 'react';
import Customer from '../../graphql/entities/customer/Customer';
import CustomerForm from '../../views/customer/CustomerForm';
import attributeQuery from '../../graphql/queries/customer/CustomerAttributeConfigListQuery.graphql';
import Formatters from '../../components/grid-addons/formatters';
import { usePredefinedFilters } from '../../getPredefinedFilters';
import { useWorkspace } from '../../graphql/hooks/useWorkspace';
import { useAttributesForWorkspace } from '../../components/Workspace/useAttributesForWorkspace';
import Workspace from '../../components/Workspace';
import TextFieldFormatter from '../../components/grid-addons/formatters/TextFieldFormatter';
import { ICustomer } from '../../graphql/schema/p4types';
import { IWorkspaceColumnOverride, IWorkspaceProps } from '../../@types/workspaceTypes';
import { Paths } from '../../@types/keyOfNested';

import { WorkspaceActionButtons } from '../../components/Workspace/WorkspaceActionButtons';
import { useCustomerWorkspaceActions } from '../../actions/customer/CustomerWorkspaceAction';
import { EntityType } from '../../@types/entityTypes';

/*
 * A text field formatter for workspace fields that hides the value (Displays *'s) unless a provided condition is met.
 * Must set options: {condition: func} on the column override - Takes rowData and should return true/false.
 * */
export const HiddenTextFormatter = ({ value: potentialValue, ...rest }) => {
  const value = rest.column.options?.condition?.(rest.dependentValues?.rowData)
    ? potentialValue
    : '*'.repeat(potentialValue?.length ?? 0);
  return <TextFieldFormatter {...rest} value={value} />;
};

const CUSTOMER_FIELD_NAMES: Array<Paths<ICustomer>> = [
  'reference',
  'name',
  'address.line1',
  'email',
  'balance.balance',
];

export const CUSTOMER_DEFAULT_COLUMN_OVERRIDES: IWorkspaceColumnOverride = {
  reference: {
    width: 125,
    formatter: Formatters.PrimaryFieldFormatter,
  },
  'balance.balance': {
    name: 'Total Balance',
    width: 160,
    formatter: Formatters.NumericFormatter,
    options: {
      format: { decimals: 2 },
    },
  },
  onlyInvoiceFullyDispatched: {
    name: 'Invoice Fully Dispatched Only',
    formatter: Formatters.BooleanFormatter,
  },
  telephoneNumber: {
    formatter: HiddenTextFormatter,
    options: {
      condition: (rowData) => rowData?.allowPhoneCalls,
    },
  },
  telephoneNumber2: {
    formatter: HiddenTextFormatter,
    options: {
      condition: (rowData) => rowData?.allowPhoneCalls,
    },
  },
  mobileNumber: {
    formatter: HiddenTextFormatter,
    options: {
      condition: (rowData) => rowData?.allowPhoneCalls,
    },
  },
  faxNumber: {
    formatter: HiddenTextFormatter,
    options: {
      condition: (rowData) => rowData?.allowFax,
    },
  },
  automatedCreditControlWorkflow: {
    name: 'ACC Workflow',
  },
  excludeFromAutomatedCreditControl: {
    name: 'Excluded from ACC',
    formatter: Formatters.BooleanFormatter,
  },
  defaultVatInclusive: {
    name: 'Default VAT Inclusive',
  },
  'vatBusinessCategory.description': {
    name: 'VAT Business Category - Description',
  },
  vatRegistrationNumber: {
    name: 'VAT Registration Number',
  },
  'customerAcknowledgementDefault.documentRequired': {
    name: 'Acknowledgements - Required',
  },
  'customerAcknowledgementDefault.output': {
    name: 'Acknowledgements - Output',
  },
  'customerAcknowledgementDefault.layout.name': {
    name: 'Acknowledgements - Layout',
  },
  'customerQuotationDocumentDefault.documentRequired': {
    name: 'Quotations - Required',
  },
  'customerQuotationDocumentDefault.output': {
    name: 'Quotations - Output',
  },
  'customerQuotationDocumentDefault.layout.name': {
    name: 'Quotations - Layout',
  },
  'customerDeliveryNoteDefault.documentRequired': {
    name: 'Delivery Notes - Required',
  },
  'customerDeliveryNoteDefault.output': {
    name: 'Delivery Notes - Output',
  },
  'customerDeliveryNoteDefault.layout.name': {
    name: 'Delivery Notes - Layout',
  },
  'customerStatementDocumentDefault.documentRequired': {
    name: 'Statements - Required',
  },
  'customerStatementDocumentDefault.output': {
    name: 'Statements - Output',
  },
  'customerStatementDocumentDefault.layout.name': {
    name: 'Statements - Layout',
  },
  'customerConformityCertificateDefault.documentRequired': {
    name: 'Certificate of Conformity - Required',
  },
  'customerConformityCertificateDefault.output': {
    name: 'Certificate of Conformity - Output',
  },
  'customerConformityCertificateDefault.layout.name': {
    name: 'Certificate of Conformity - Layout',
  },
  'customerInvoiceDocumentDefault.documentRequired': {
    name: 'Sales Invoice - Required',
  },
  'customerInvoiceDocumentDefault.output': {
    name: 'Sales Invoice - Output',
  },
  'customerInvoiceDocumentDefault.layout.name': {
    name: 'Sales Invoice - Layout',
  },
  'customerInvoiceDocumentDefault.invoiceConsolidationOption': {
    name: 'Sales Invoice - Consolidation',
  },
  'customerProformaInvoiceDocumentDefault.documentRequired': {
    name: 'Proforma Invoice - Required',
  },
  'customerProformaInvoiceDocumentDefault.output': {
    name: 'Proforma Invoice - Output',
  },
  'customerProformaInvoiceDocumentDefault.layout.name': {
    name: 'Proforma Invoice - Layout',
  },
  'customerDataSheetDocumentDefault.documentRequired': {
    name: 'Data Sheet - Required',
  },
  'customerDataSheetDocumentDefault.layout.name': {
    name: 'Data Sheet - Layout',
  },
  'customerDispatchEmailDefault.documentRequired': {
    name: 'Dispatch Email - Required',
  },
  'customerDispatchEmailDefault.layout.name': {
    name: 'Dispatch Email - Layout',
  },
  'customerPaymentReceiptDefault.documentRequired': {
    name: 'Payment Receipt - Required',
  },
  'customerPaymentReceiptDefault.output': {
    name: 'Payment Receipt - Output',
  },
  'customerPaymentReceiptDefault.layout.name': {
    name: 'Payment Receipt - Layout',
  },
  'customerCreditDocumentDefault.documentRequired': {
    name: 'Sales Credit - Required',
  },
  'customerCreditDocumentDefault.output': {
    name: 'Sales Credit - Output',
  },
  'customerCreditDocumentDefault.layout.name': {
    name: 'Sales Credit - Layout',
  },
  'customerOverdueAccountDefault.documentRequired': {
    name: 'Overdue Account - Required',
  },
  'customerOverdueAccountDefault.output': {
    name: 'Overdue Account - Output',
  },
  'customerOverdueAccountDefault.layout.name': {
    name: 'Overdue Account - Layout',
  },
  windsorFrameworkUkims: {
    name: 'Windsor Framework UKIMS',
  },
};

const CustomerCellRenderer = ({ dependentValues }) => {
  if (!dependentValues.rowData?.node) return <div />;
  const { reference, name } = dependentValues.rowData.node;
  return (
    <div>
      <div className="cell-primary">{reference}</div>
      <div className="cell-data">{name}</div>
    </div>
  );
};

const CustomerWorkspaceActions = (props) => {
  const [customerWorkspaceActions, modals] = useCustomerWorkspaceActions();
  return (
    <>
      {modals}
      <WorkspaceActionButtons {...props} actionButtons={customerWorkspaceActions} />
    </>
  );
};

const CustomerWorkspace = ({ entity, ...props }: IWorkspaceProps<ICustomer> & { entity?: EntityType }) => {
  const { formUrl, workspace, type, title } = { ...Customer, ...entity };

  const predefinedFilters = usePredefinedFilters({
    history: props.history,
    predefinedFilters: props.predefinedFilters,
  });

  const attributeColumns = useAttributesForWorkspace(attributeQuery);
  const workspaceProps = useWorkspace<ICustomer>(props, Customer, {
    filters: predefinedFilters,
    columns: props.columns || CUSTOMER_FIELD_NAMES,
    columnOverrides: CUSTOMER_DEFAULT_COLUMN_OVERRIDES,
    customColumns: attributeColumns,
    initialSortColumn: 'reference',
  });

  return (
    <Workspace<ICustomer>
      title={title}
      formUrl={formUrl}
      entityType={type}
      cellRenderer={CustomerCellRenderer}
      detailView={CustomerForm}
      defaultQuery={workspace}
      duplicate={true}
      {...workspaceProps}
      {...props}
      entity={Customer}
      workspaceActionButtonsHOC={props.workspaceActionButtonsHOC || CustomerWorkspaceActions}
    />
  );
};

export default CustomerWorkspace;
